import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Image,
  StyleSheet,
  Text,
  TouchableOpacity,
  useColorScheme,
  View,
} from 'react-native';
import {
  APP_DOWNLOAD_LINKS,
  buildDesktopProtocolUrl,
  parseDesktopHandoffSearch,
} from '../utilities/desktopAppHandoff';

export default function OpenDesktopAppScreen() {
  const dark = useColorScheme() === 'dark';
  const palette = dark
    ? { background: '#11131A', card: '#1D2029', text: '#F7F7FA', muted: '#A5A8B2', border: '#343842', primary: '#EE6C4C' }
    : { background: '#F5F5F1', card: '#FFFFFF', text: '#191B22', muted: '#686C76', border: '#DDDDE2', primary: '#EE6C4C' };
  const styles = useMemo(() => makeStyles(palette), [dark]); // eslint-disable-line react-hooks/exhaustive-deps
  const attempted = useRef(false);
  const target = typeof window !== 'undefined'
    ? parseDesktopHandoffSearch(window.location.search)
    : null;
  const protocolUrl = target
    ? buildDesktopProtocolUrl(target.projectId, target.accountId!, target.conversationId)
    : null;

  const openDesktop = useCallback(() => {
    if (!protocolUrl || typeof window === 'undefined') return;
    window.location.href = protocolUrl;
  }, [protocolUrl]);

  useEffect(() => {
    if (typeof document !== 'undefined') document.title = 'Opening Eleplan';
    if (!protocolUrl || attempted.current || typeof window === 'undefined') return;
    attempted.current = true;
    // The page itself was reached by a direct user click from chat. Keep the
    // attempt close to that activation so Chromium can present its native
    // "Open Eleplan?" confirmation, then leave this page behind as the retry /
    // download fallback if the protocol has no installed handler.
    const timer = window.setTimeout(openDesktop, 100);
    return () => window.clearTimeout(timer);
  }, [openDesktop, protocolUrl]);

  const visit = (url: string) => {
    if (typeof window !== 'undefined') window.location.assign(url);
  };

  const returnToWeb = () => {
    if (typeof window === 'undefined') return;
    try {
      const referrer = typeof document !== 'undefined' ? document.referrer : '';
      if (
        referrer
        && new URL(referrer).origin === window.location.origin
        && window.history.length > 1
      ) {
        window.history.back();
        return;
      }
    } catch {
      // A missing or malformed referrer is not trusted as a navigation target.
    }
    visit('/');
  };

  return (
    <View style={styles.page}>
      <View style={styles.card}>
        <Image source={require('../../assets/icon.png')} style={styles.icon} accessibilityLabel="Eleplan" />
        <Text style={styles.title}>{protocolUrl ? 'Opening Eleplan' : 'Chat link unavailable'}</Text>
        <Text style={styles.body}>
          {protocolUrl
            ? "We're opening this chat in the Eleplan desktop app. If it doesn't open, try again or download the app."
            : 'This handoff link does not name a valid care-plan chat. Return to Eleplan and try the button again.'}
        </Text>

        <View style={styles.actions}>
          {protocolUrl && (
            <TouchableOpacity
              style={styles.primaryButton}
              onPress={openDesktop}
              accessibilityRole="button"
              accessibilityLabel="Open Eleplan desktop app"
            >
              <Text style={styles.primaryLabel}>Open Eleplan</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            style={styles.secondaryButton}
            onPress={() => visit(APP_DOWNLOAD_LINKS.desktop)}
            accessibilityRole="link"
            accessibilityLabel="Download Eleplan for desktop"
          >
            <Text style={styles.secondaryLabel}>Download desktop app</Text>
          </TouchableOpacity>
        </View>

        <TouchableOpacity
          style={styles.backLink}
          onPress={returnToWeb}
          accessibilityRole="link"
          accessibilityLabel="Return to Eleplan web"
        >
          <Text style={styles.backLabel}>Return to Eleplan web</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

function makeStyles(colors: Record<string, string>) {
  return StyleSheet.create({
    page: {
      flex: 1,
      minHeight: '100vh' as any,
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      backgroundColor: colors.background,
    },
    card: {
      width: '100%',
      maxWidth: 560,
      alignItems: 'center',
      paddingHorizontal: 36,
      paddingVertical: 44,
      borderRadius: 24,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: colors.border,
      backgroundColor: colors.card,
      shadowColor: '#000',
      shadowOpacity: 0.12,
      shadowRadius: 30,
      shadowOffset: { width: 0, height: 14 },
    },
    icon: {
      width: 72,
      height: 72,
      borderRadius: 17,
      marginBottom: 24,
    },
    title: {
      color: colors.text,
      fontSize: 30,
      lineHeight: 38,
      fontWeight: '700',
      textAlign: 'center',
    },
    body: {
      color: colors.muted,
      fontSize: 15,
      lineHeight: 23,
      textAlign: 'center',
      maxWidth: 450,
      marginTop: 10,
    },
    actions: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: 10,
      marginTop: 28,
    },
    primaryButton: {
      backgroundColor: colors.primary,
      borderRadius: 999,
      paddingHorizontal: 22,
      paddingVertical: 12,
    },
    primaryLabel: {
      color: '#FFFFFF',
      fontSize: 14,
      fontWeight: '700',
    },
    secondaryButton: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 999,
      paddingHorizontal: 22,
      paddingVertical: 12,
    },
    secondaryLabel: {
      color: colors.text,
      fontSize: 14,
      fontWeight: '600',
    },
    backLink: {
      marginTop: 22,
      padding: 6,
    },
    backLabel: {
      color: colors.muted,
      fontSize: 13,
      textDecorationLine: 'underline',
    },
  });
}
